import React from 'react'
import PropTypes from 'prop-types'
import BaseProductCarousel from '../baseProductCarousel'
import {useVickyAIRecommendations} from '../../../../hooks/use-vicky-pdp-recommendations'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * RecommendationsCarousel - Generic component that handles product recommendations
 * from any source (VickyAI, Salesforce, or directly injected data).
 *
 * @param {Object} props
 * @param {string} props.Title - Carousel title from CMS
 * @param {Object} props.TitleStyle - CMS title styling object
 * @param {string} props.AIModel - Type of VickyAI model ('recommendations', 'similars', 'bought-together') - optional
 * @param {string} props.productSku - Product SKU (required for similars and bought-together)
 * @param {string|number} props.TileNumber - Number of tiles on mobile (from CMS)
 * @param {string|number} props.TabletTileNumber - Number of tiles on tablet (from CMS)
 * @param {string|number} props.DesktopTileNumber - Number of tiles on desktop (from CMS)
 * @param {string|number} props.TileSlideNumber - Desktop slides per view override (from CMS)
 * @param {number} props.slidesToShowMobile - Fallback number of slides on mobile
 * @param {number} props.slidesToShowDesktop - Fallback number of slides on desktop
 * @param {string} props.carouselID - Unique identifier for carousel navigation
 * @param {boolean} props.showAsGrid - Whether to display as grid layout
 * @param {Function} props.onSkusUpdate - Callback to notify parent about SKU count
 * @param {Object} props._recommendationData - SSR-injected recommendation data (optional)
 * @param {Object} props.recommendationData - Alternative prop name for injected data (optional)
 */
const RecommendationsCarousel = ({
    Title,
    CarouselTitle,
    TitleStyle,
    VickyProductCarouselBlockCarouselTitleStyle,
    ProductTitleStyle,
    VickyProductCarouselBlockProductTitleStyle,
    ProductPriceStyle,
    VickyProductCarouselBlockProductPriceStyle,
    AIModel,
    productSku,
    TileNumber,
    TabletTileNumber,
    DesktopTileNumber,
    TileSlideNumber,
    slidesToShowMobile = 2.3,
    slidesToShowTablet = 3.5,
    slidesToShowDesktop = 4.5,
    carouselID = 'recommendations',
    showAsGrid = false,
    onSkusUpdate,
    _recommendationData,
    recommendationData,
    onProductClick,
    HideOnDevices,
    CarouselCTATile,
    isMiniCart = false,
    hydrationCount = 0 // From withViewportHydration HOC - triggers re-measurement
}) => {
    const {app: appConfig} = getConfig()
    // Use CMS property names if short names not provided
    const effectiveTitle = Title || CarouselTitle || 'YOU MAY ALSO LIKE'
    const effectiveTitleStyle = TitleStyle || VickyProductCarouselBlockCarouselTitleStyle
    const effectiveProductTitleStyle =
        ProductTitleStyle || VickyProductCarouselBlockProductTitleStyle
    const effectiveProductPriceStyle =
        ProductPriceStyle || VickyProductCarouselBlockProductPriceStyle
    // Check if we have SSR-injected data
    const hasInjectedData = Boolean(_recommendationData || recommendationData)
    const injectedData = _recommendationData || recommendationData || {}

    // Fetch client-side data if no injected data
    // Always call hook unconditionally (React Rules of Hooks)
    // Skip fetching if AIModel requires productSku but it's missing (invalid combination)
    const needsProductSku = AIModel === 'bought-together' || AIModel === 'similars'
    const shouldSkip = hasInjectedData || (needsProductSku && !productSku)

    const clientFetchData = useVickyAIRecommendations(
        shouldSkip ? 'skip' : AIModel,
        productSku,
        'RecommendationsCarousel'
    )

    // Use injected data if available, otherwise use client-fetched data
    const rawProducts = hasInjectedData ? injectedData.products : clientFetchData.products
    // Never show loading state when we have SSR-injected data
    const isLoading = hasInjectedData ? false : clientFetchData.isLoading

    // Fix image data structure for ProductTile compatibility
    const products = React.useMemo(() => {
        if (!rawProducts || !Array.isArray(rawProducts)) return []

        return rawProducts.map((product) => {
            if (product.imageGroups && Array.isArray(product.imageGroups)) {
                const fixedImageGroups = product.imageGroups.map((group) => ({
                    ...group,
                    images:
                        group.images?.map((img) => ({
                            ...img,
                            // Ensure disBaseLink is always set (prefer disBaseLink, fallback to link)
                            disBaseLink: img.disBaseLink || img.link || '',
                            // Keep link as well for compatibility
                            link: img.link || img.disBaseLink || ''
                        })) || []
                }))

                return {
                    ...product,
                    imageGroups: fixedImageGroups
                }
            }
            return product
        })
    }, [rawProducts])

    return (
        <>
            {appConfig?.VICKY_ENABLED === 'true' && products?.length > 0 && (
                <BaseProductCarousel
                    Title={effectiveTitle}
                    TitleStyle={effectiveTitleStyle}
                    ProductTitleStyle={effectiveProductTitleStyle}
                    ProductPriceStyle={effectiveProductPriceStyle}
                    products={products}
                    isLoading={isLoading}
                    TileNumber={isMiniCart ? null : TileNumber}
                    TabletTileNumber={isMiniCart ? null : TabletTileNumber}
                    DesktopTileNumber={isMiniCart ? 2 : DesktopTileNumber}
                    TileSlideNumber={TileSlideNumber}
                    slidesToShowMobile={slidesToShowMobile}
                    slidesToShowTablet={slidesToShowTablet}
                    slidesToShowDesktop={slidesToShowDesktop}
                    carouselID={carouselID}
                    showAsGrid={isMiniCart ? 'always' : showAsGrid}
                    onSkusUpdate={onSkusUpdate}
                    onProductClick={onProductClick}
                    HideOnDevices={HideOnDevices}
                    CarouselCTATile={CarouselCTATile}
                    hydrationCount={hydrationCount}
                />
            )}
        </>
    )
}

RecommendationsCarousel.propTypes = {
    Title: PropTypes.string,
    CarouselTitle: PropTypes.string,
    TitleStyle: PropTypes.object,
    VickyProductCarouselBlockCarouselTitleStyle: PropTypes.object,
    ProductTitleStyle: PropTypes.object,
    VickyProductCarouselBlockProductTitleStyle: PropTypes.object,
    ProductPriceStyle: PropTypes.object,
    VickyProductCarouselBlockProductPriceStyle: PropTypes.object,
    AIModel: PropTypes.oneOf(['recommendations', 'similars', 'bought-together']),
    productSku: PropTypes.string,
    TileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TabletTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    DesktopTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TileSlideNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slidesToShowMobile: PropTypes.number,
    slidesToShowTablet: PropTypes.number,
    slidesToShowDesktop: PropTypes.number,
    carouselID: PropTypes.string,
    showAsGrid: PropTypes.bool,
    onSkusUpdate: PropTypes.func,
    _recommendationData: PropTypes.shape({
        products: PropTypes.array,
        isLoading: PropTypes.bool
    }),
    recommendationData: PropTypes.shape({
        products: PropTypes.array,
        isLoading: PropTypes.bool
    }),
    onProductClick: PropTypes.func,
    HideOnDevices: PropTypes.string,
    CarouselCTATile: PropTypes.object,
    isMiniCart: PropTypes.bool,
    hydrationCount: PropTypes.number
}

export default RecommendationsCarousel
